import {
  ApolloError,
  FetchResult,
  gql,
  useApolloClient,
  useMutation,
} from '@apollo/client';

import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const deleteRegistrySubscriptionMutation = gql`
  mutation DeleteRegistrySubscriptionMutation(
    $graphId: ID!
    $subscriptionId: ID!
  ) {
    service(id: $graphId) {
      deleteRegistrySubscription(id: $subscriptionId)
    }
  }
`;

export const deleteProposalLifecycleSubscriptionMutation = gql`
  mutation DeleteProposalLifecycleSubscriptionMutation(
    $graphId: ID!
    $input: DeleteProposalLifecycleSubscriptionInput!
  ) {
    service(id: $graphId) {
      deleteProposalLifecycleSubscription(input: $input) {
        ... on DeleteProposalLifecycleSubscriptionSuccess {
          id
        }
      }
    }
  }
`;

export function useDeleteSubscription({
  graphId,
  subscription,
  onCompleted: onCompletedCallback,
}: {
  onCompleted: () => void;
  graphId: string;
  subscription: GraphQLTypes.ChannelCardChannelFragment_subscriptions;
}): {
  loading?: boolean;
  error?: ApolloError;
  deleteSubscription: () => Promise<FetchResult | undefined>;
} {
  const client = useApolloClient();
  const onCompleted = () => {
    client.cache.evict({
      id: client.cache.identify({
        __typename: subscription.__typename,
        id: subscription.id,
      }),
    });
    onCompletedCallback();
  };

  const [
    deleteScheduledSummary,
    { loading: loadingScheduledSummary, error: errorScheduledSummary },
  ] = useMutation<
    GraphQLTypes.DeleteScheduledSummaryMutation,
    GraphQLTypes.DeleteScheduledSummaryMutationVariables
  >(
    gql`
      mutation DeleteScheduledSummaryMutation(
        $graphId: ID!
        $subscriptionId: ID!
      ) {
        service(id: $graphId) {
          deleteScheduledSummary(id: $subscriptionId)
        }
      }
    `,
    {
      variables: { graphId, subscriptionId: subscription.id },
      onCompleted,
    },
  );

  const [
    deleteRegistrySubscription,
    { loading: loadingRegistrySubscription, error: errorRegistrySubscription },
  ] = useMutation<
    GraphQLTypes.DeleteRegistrySubscriptionMutation,
    GraphQLTypes.DeleteRegistrySubscriptionMutationVariables
  >(deleteRegistrySubscriptionMutation, {
    variables: { graphId, subscriptionId: subscription.id },
    onCompleted,
  });

  const [
    deleteProposalLifecycleSubscription,
    {
      loading: loadingProposalLifecycleSubscription,
      error: errorProposalLifecycleSubscription,
    },
  ] = useMutation<
    GraphQLTypes.DeleteProposalLifecycleSubscriptionMutation,
    GraphQLTypes.DeleteProposalLifecycleSubscriptionMutationVariables
  >(deleteProposalLifecycleSubscriptionMutation, {
    variables: { graphId, input: { id: subscription.id } },
    onCompleted,
  });

  const typename = subscription.__typename;
  return {
    loading:
      loadingScheduledSummary ||
      loadingRegistrySubscription ||
      loadingProposalLifecycleSubscription,
    error:
      errorScheduledSummary ||
      errorRegistrySubscription ||
      errorProposalLifecycleSubscription,
    deleteSubscription: (() => {
      switch (typename) {
        case 'ScheduledSummary':
          return deleteScheduledSummary;
        case 'RegistrySubscription':
        case 'SchemaPublishSubscription':
        case 'CompositionStatusSubscription':
          return deleteRegistrySubscription;
        case 'ProposalLifecycleSubscription':
          return deleteProposalLifecycleSubscription;
        case 'QueryTrigger':
          return async () => undefined;
        default: {
          return assertUnreachableOrReturnDefault(
            typename,
            async () => undefined,
          );
        }
      }
    })(),
  };
}
